import ErrorPage from '@pages/other/ErrorPage/ErrorPage';
import MainLayout from '../layouts/MainLayout/MainLayout';
import paths, { settingsPaths } from './paths';
import { permissionSets, RequirePermissions } from '@app/permissions';
import { permissions } from '@app/permissions/permissions';
import RedirectForbidden from '@app/permissions/RedirectForbidden';
import { RouteObject, Navigate } from 'react-router-dom';
import { lazyComponent, lazyWrapComponent } from './lazy';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import { Spinner } from '@servicetitan/design-system';
import { useGlobalRecordingOverride } from '@api/queries/recording/hooks/useGlobalRecordingOverride';
import { FeatureErrorBoundary } from '@components/ErrorBoundary';
import VoiceAgentDashboardRouteGuard from '@pages/reports/VoiceAgentDashboardPage/VoiceAgentDashboardRouteGuard';
import RequireFeature from './RequireFeature';
import useAuth from '@modules/AuthModule/hooks/useAuth';

const mainRoutes: RouteObject[] = [
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/forbidden',
        lazy: () =>
          lazyComponent(import('@pages/other/ForbiddenPage/ForbiddenPage')),
      },
      {
        path: '',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(import('@pages/agent/InboxPage/InboxPage')),
            ({ children }) => (
              <FeatureErrorBoundary featureName='Inbox'>
                {children}
              </FeatureErrorBoundary>
            ),
          ),
      },
      // navigate to inbox if user tries to access /signin-oidc by clicking back button, etc. by mistake
      // this is a workaround for the issue where the user is navigating to /signin-oidc after signing by mistake
      // swiping fingers on the touchpad or clicking back button
      {
        path: '/signin-oidc',
        element: <Navigate to='/' replace />,
      },
      {
        path: 'monitoring/*',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import(
                '@pages/monitoring/CallCenterMonitoringPage/CallCenterMonitoringPage'
              ),
            ),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.general.QUEUE_MONITORING}
                permissionsMatch='any'
                unavailable={<RedirectForbidden />}
              >
                <FeatureErrorBoundary featureName='Call Center Monitoring'>
                  {children}
                </FeatureErrorBoundary>
              </RequirePermissions>
            ),
          ),
      },
      {
        path: paths.queues,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(import('@pages/monitoring/QueuesPage/QueuesPage')),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.general.QUEUE_MONITORING}
                permissionsMatch='any'
                unavailable={<RedirectForbidden />}
              >
                <FeatureErrorBoundary featureName='Queues'>
                  {children}
                </FeatureErrorBoundary>
              </RequirePermissions>
            ),
          ),
      },

      {
        path: 'conversations/:interactionId?',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import('@pages/monitoring/ConversationsPage/ConversationsPage'),
            ),
            ({ children }) => (
              <FeatureErrorBoundary featureName='Conversations'>
                {children}
              </FeatureErrorBoundary>
            ),
          ),
      },
      {
        path: paths.voiceIntelligenceCalls,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import(
                '@pages/voiceintelligence/VoiceIntelligenceCallAnalysisPage'
              ),
            ),
            ({ children }) => (
              <RequireFeature
                flagName={FeatureFlagKeys.featureVoiceIntelligence}
                shouldRedirectIfDisabled={true}
              >
                <RequirePermissions
                  permissions={[
                    permissions.general.VOICE_INTELLIGENCE.CALL_ANALYSIS_VIEW,
                  ]}
                  permissionsMatch='all'
                  unavailable={<RedirectForbidden />}
                >
                  <FeatureErrorBoundary featureName='Voice Intelligence'>
                    {children}
                  </FeatureErrorBoundary>
                </RequirePermissions>
              </RequireFeature>
            ),
          ),
      },
      {
        path: paths.voiceIntelligenceInsights,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import('@pages/voiceintelligence/VoiceIntelligenceInsightsPage'),
            ),
            ({ children }) => (
              <RequireFeature
                flagName={FeatureFlagKeys.featureVoiceIntelligence}
                shouldRedirectIfDisabled={true}
              >
                <RequirePermissions
                  permissions={[
                    permissions.general.VOICE_INTELLIGENCE.INSIGHTS_VIEW,
                  ]}
                  permissionsMatch='all'
                  unavailable={<RedirectForbidden />}
                >
                  <FeatureErrorBoundary featureName='Voice Intelligence'>
                    {children}
                  </FeatureErrorBoundary>
                </RequirePermissions>
              </RequireFeature>
            ),
          ),
      },
      {
        path: 'urgentfollowups/abandonedcalls/:abandonedCallFollowUpId?',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import(
                '@pages/urgentfollowups/AbandonedCallsPage/AbandonedCallsPage'
              ),
            ),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.general.FOLLOW_UPS.ABANDONED_CALLS}
                permissionsMatch='all'
                unavailable={<RedirectForbidden />}
              >
                <FeatureErrorBoundary featureName='Abandoned Calls'>
                  {children}
                </FeatureErrorBoundary>
              </RequirePermissions>
            ),
          ),
      },
      {
        path: 'urgentfollowups/secondchanceleads/:secondChanceLeadFollowUpId?',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import(
                '@pages/urgentfollowups/SecondChanceLeadsPage/SecondChanceLeadsPage'
              ),
            ),
            ({ children }) => (
              <RequirePermissions
                permissions={
                  permissionSets.general.FOLLOW_UPS.SECOND_CHANCE_LEADS
                }
                permissionsMatch='all'
                unavailable={<RedirectForbidden />}
              >
                <FeatureErrorBoundary featureName='Second Chance Leads'>
                  {children}
                </FeatureErrorBoundary>
              </RequirePermissions>
            ),
          ),
      },
      {
        path: 'urgentfollowups/voicemails/:voicemailFollowUpId?',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import('@pages/urgentfollowups/VoicemailsPage/VoicemailsPage'),
            ),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.general.FOLLOW_UPS.VOICEMAIL}
                permissionsMatch='all'
                unavailable={<RedirectForbidden />}
              >
                <FeatureErrorBoundary featureName='Voicemails'>
                  {children}
                </FeatureErrorBoundary>
              </RequirePermissions>
            ),
          ),
      },
      {
        path: 'urgentfollowups/virtualagentfollowups/:virtualAgentFollowUpId?',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import(
                '@pages/urgentfollowups/VirtualAgentFollowUpsPage/VirtualAgentFollowUpsPage'
              ),
            ),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.general.FOLLOW_UPS.VIRTUAL_AGENT}
                permissionsMatch='all'
                unavailable={<RedirectForbidden />}
              >
                <FeatureErrorBoundary featureName='Virtual Agent Follow-ups'>
                  {children}
                </FeatureErrorBoundary>
              </RequirePermissions>
            ),
          ),
      },
      {
        path: paths.voiceAgentDashboard,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import(
                '@pages/reports/VoiceAgentDashboardPage/VoiceAgentDashboardMfeWrapper'
              ),
            ),
            ({ children }) => (
              <VoiceAgentDashboardRouteGuard>
                {children}
              </VoiceAgentDashboardRouteGuard>
            ),
          ),
      },
      {
        path: 'profile',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(import('@pages/agent/ProfilePage/ProfilePage')),
            ({ children }) => (
              <FeatureErrorBoundary featureName='Profile'>
                {children}
              </FeatureErrorBoundary>
            ),
          ),
      },
      {
        path: 'debug',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(import('@pages/other/DebugPage/DebugPage')),
            ({ children }) => (
              <FeatureErrorBoundary featureName='Debug'>
                {children}
              </FeatureErrorBoundary>
            ),
          ),
      },
      {
        path: paths.settings,
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(import('@pages/settings/SettingsLayout')),
            ({ children }) => (
              <RequirePermissions
                permissions={permissionSets.admin.ALL}
                permissionsMatch='any'
                unavailable={<RedirectForbidden />}
              >
                <FeatureErrorBoundary featureName='Settings Layout'>
                  {children}
                </FeatureErrorBoundary>
              </RequirePermissions>
            ),
          ),
        children: [
          {
            path: '',
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import('@pages/settings/SettingsPage/SettingsPage'),
                ),
                ({ children }) => (
                  <RequirePermissions
                    permissions={permissionSets.admin.ALL}
                    permissionsMatch='any'
                    unavailable={<RedirectForbidden />}
                  >
                    <FeatureErrorBoundary featureName='Settings'>
                      {children}
                    </FeatureErrorBoundary>
                  </RequirePermissions>
                ),
              ),
          },
          {
            path: `${settingsPaths.userManagement}/:userId?`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/UserManagementPage/UserManagementPage'
                  ),
                ),
                ({ children }) => (
                  <RequirePermissions
                    permissions={permissionSets.admin.USER_MANAGEMENT}
                    permissionsMatch='any'
                    unavailable={<RedirectForbidden />}
                  >
                    <FeatureErrorBoundary featureName='User Management'>
                      {children}
                    </FeatureErrorBoundary>
                  </RequirePermissions>
                ),
              ),
          },
          {
            path: `${settingsPaths.workflows}/:workflowId?`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/WorkflowsPage/WorkflowManagementPage'
                  ),
                ),
                ({ children }) => (
                  <RequirePermissions
                    permissions={permissionSets.admin.TELECOM_WORKFLOWS}
                    permissionsMatch='any'
                    unavailable={<RedirectForbidden />}
                  >
                    <FeatureErrorBoundary featureName='Workflow Management'>
                      {children}
                    </FeatureErrorBoundary>
                  </RequirePermissions>
                ),
              ),
          },
          {
            path: `${settingsPaths.callContinuity}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/CallContinuityPage/CallContinuityPage'
                  ),
                ),
                ({ children }) => (
                  <RequireFeature
                    flagName={FeatureFlagKeys.featureCallContinuity}
                    shouldRedirectIfDisabled={true}
                  >
                    <RequirePermissions
                      permissions={
                        permissionSets.admin.CALL_CONTINUITY_EDIT_NETWORK
                      }
                      permissionsMatch='all'
                      unavailable={<RedirectForbidden />}
                    >
                      <FeatureErrorBoundary featureName='Call Continuity'>
                        {children}
                      </FeatureErrorBoundary>
                    </RequirePermissions>
                  </RequireFeature>
                ),
              ),
          },
          {
            path: `${settingsPaths.queueManagement}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/QueueManagementPage/QueueManagementPage'
                  ),
                ),
                ({ children }) => {
                  const {
                    [FeatureFlagKeys.featureQueueManagementPage]:
                      featureQueueManagementPage,
                  } = useFeatureFlags();

                  if (featureQueueManagementPage === undefined) {
                    return <Spinner />;
                  }

                  if (!featureQueueManagementPage) {
                    return <RedirectForbidden />;
                  }

                  return (
                    <RequirePermissions
                      permissions={[permissions.admin.TELECOM_WORKFLOWS.VIEW]}
                      unavailable={<RedirectForbidden />}
                    >
                      <FeatureErrorBoundary featureName='Queue Management'>
                        {children}
                      </FeatureErrorBoundary>
                    </RequirePermissions>
                  );
                },
              ),
          },
          {
            path: `${settingsPaths.presetDaysTimes}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import('@pages/settings/PresetDaysTimes/PresetDaysTimesPage'),
                ),
                ({ children }) => (
                  <FeatureErrorBoundary featureName='Preset Days and Times'>
                    <RequirePermissions
                      permissions={permissionSets.admin.TELECOM_WORKFLOWS}
                      permissionsMatch='any'
                      unavailable={<RedirectForbidden />}
                    >
                      {children}
                    </RequirePermissions>
                  </FeatureErrorBoundary>
                ),
              ),
          },
          // Virtual Agents page
          {
            path: `${settingsPaths.virtualAgents}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/VirtualAgentsPage/VirtualAgentSettingsPageWrapper'
                  ),
                ),
                ({ children }) => (
                  <FeatureErrorBoundary featureName='Virtual Agents'>
                    <RequirePermissions
                      permissions={permissionSets.admin.VIRTUAL_AGENT}
                      permissionsMatch='any'
                      unavailable={<RedirectForbidden />}
                    >
                      {children}
                    </RequirePermissions>
                  </FeatureErrorBoundary>
                ),
              ),
          },
          // Feature Management > Call Analysis page
          {
            path: `${settingsPaths.fmCallAnalysis}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/FeatureManagement/CallAnalysisPage/CallAnalysisPage'
                  ),
                ),
                ({ children }) => {
                  return (
                    <FeatureErrorBoundary featureName='Call Analysis'>
                      <RequirePermissions
                        permissions={
                          permissionSets.admin.FEATURE_MANAGEMENT_CALL_ANALYSIS
                        }
                        permissionsMatch='any'
                        unavailable={<RedirectForbidden />}
                      >
                        {children}
                      </RequirePermissions>
                    </FeatureErrorBoundary>
                  );
                },
              ),
          },

          // Feature Management > Data Integration page
          {
            path: `${settingsPaths.dataIntegration}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/FeatureManagement/DataIntegrationPage/DataIntegrationPage'
                  ),
                ),
                ({ children }) => {
                  const {
                    [FeatureFlagKeys.featureDataIntegrationEnabled]:
                      featureDataIntegrationEnabled,
                  } = useFeatureFlags();

                  if (
                    // Prevent Redirecting to Forbidden before Feature Flag is Loaded
                    featureDataIntegrationEnabled === undefined
                  ) {
                    return <Spinner />;
                  }

                  // Feature management data integration - atleast one form in the Data Integration page must be enabled
                  if (!featureDataIntegrationEnabled) {
                    return <RedirectForbidden />;
                  }

                  return (
                    <FeatureErrorBoundary featureName='Data Integration'>
                      <RequirePermissions
                        permissions={permissionSets.admin.FEATURE_MANAGEMENT}
                        permissionsMatch='any'
                        unavailable={<RedirectForbidden />}
                      >
                        {children}
                      </RequirePermissions>
                    </FeatureErrorBoundary>
                  );
                },
              ),
          },

          // Feature Management > Inbox Task Cards page
          {
            path: `${settingsPaths.inboxTaskCards}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/FeatureManagement/InboxTaskCardsPage/InboxTaskCardsPage'
                  ),
                ),
                ({ children }) => (
                  <RequirePermissions
                    permissions={permissionSets.admin.FEATURE_MANAGEMENT}
                    permissionsMatch='any'
                    unavailable={<RedirectForbidden />}
                  >
                    {children}
                  </RequirePermissions>
                ),
              ),
          },

          // Feature Management > Outbound Numbers page
          {
            path: `${settingsPaths.outboundNumbers}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/FeatureManagement/OutboundNumbersPage/OutboundNumbersPage'
                  ),
                ),
                ({ children }) => {
                  const {
                    [FeatureFlagKeys.featureConfigureOutboundSettings]:
                      featureConfigureOutboundSettings,
                  } = useFeatureFlags();

                  if (featureConfigureOutboundSettings === undefined) {
                    return <Spinner />;
                  }

                  if (!featureConfigureOutboundSettings) {
                    return <RedirectForbidden />;
                  }

                  return (
                    <FeatureErrorBoundary featureName='Outbound Numbers'>
                      <RequirePermissions
                        permissions={permissionSets.admin.PHONE_SETTINGS}
                        permissionsMatch='any'
                        unavailable={<RedirectForbidden />}
                      >
                        {children}
                      </RequirePermissions>
                    </FeatureErrorBoundary>
                  );
                },
              ),
          },

          // Feature Management > Live Services Phone Mapping page
          {
            path: `${settingsPaths.liveServicesPhoneMapping}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/FeatureManagement/LiveServicesPhoneMappingPage/LiveServicesPhoneMappingPage'
                  ),
                ),
                ({ children }) => {
                  const { user } = useAuth();
                  if (!user?.isLiveServicesNetwork) {
                    return <RedirectForbidden />;
                  }
                  return (
                    <FeatureErrorBoundary featureName='Live Services Phone Mapping'>
                      <RequirePermissions
                        permissions={permissionSets.admin.FEATURE_MANAGEMENT}
                        permissionsMatch='any'
                        unavailable={<RedirectForbidden />}
                      >
                        {children}
                      </RequirePermissions>
                    </FeatureErrorBoundary>
                  );
                },
              ),
          },

          // Manage Accounts page
          {
            path: `${settingsPaths.manageAccounts}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/ManageAccountsPage/ManageAccountsPage'
                  ),
                ),
                ({ children }) => (
                  <FeatureErrorBoundary featureName='Manage Accounts'>
                    <RequirePermissions
                      permissions={permissionSets.admin.TELECOM_MANAGE_ACCOUNT}
                      permissionsMatch='any'
                      unavailable={<RedirectForbidden />}
                    >
                      {children}
                    </RequirePermissions>
                  </FeatureErrorBoundary>
                ),
              ),
          },
          // Single Virtual Agents page
          {
            path: `${settingsPaths.virtualAgents}/:virtualAgentId`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/VirtualAgentsPage/AccountVirtualAgentSettingsPage'
                  ),
                ),
                ({ children }) => (
                  <FeatureErrorBoundary featureName='Virtual Agent Settings'>
                    <RequirePermissions
                      permissions={permissionSets.admin.VIRTUAL_AGENT}
                      permissionsMatch='any'
                      unavailable={<RedirectForbidden />}
                    >
                      {children}
                    </RequirePermissions>
                  </FeatureErrorBoundary>
                ),
              ),
          },
          {
            path: `${settingsPaths.transfersRecording}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/TransferRecordingDefaultsPage/TransferRecordingDefaultsPage'
                  ),
                ),
                ({ children }) => {
                  const {
                    [FeatureFlagKeys.featureGlobalRecordingOverridesEnabled]:
                      featureGlobalRecordingOverridesEnabled,
                  } = useFeatureFlags();

                  if (
                    // Prevent Redirecting to Forbidden before Feature Flag is Loaded
                    featureGlobalRecordingOverridesEnabled === undefined
                  ) {
                    return <Spinner />;
                  }

                  if (featureGlobalRecordingOverridesEnabled) {
                    return <RedirectForbidden />;
                  }

                  return (
                    <FeatureErrorBoundary featureName='Transfer Recording Defaults'>
                      {children}
                    </FeatureErrorBoundary>
                  );
                },
              ),
          },
          {
            path: `${settingsPaths.globalRecording}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/GlobalRecordingDefaultsPage/GlobalRecordingDefaultsPage'
                  ),
                ),
                ({ children }) => {
                  const {
                    [FeatureFlagKeys.featureGlobalRecordingOverridesEnabled]:
                      featureGlobalRecordingOverridesEnabled,
                  } = useFeatureFlags();

                  if (
                    // Prevent Redirecting to Forbidden before Feature Flag is Loaded
                    featureGlobalRecordingOverridesEnabled === undefined
                  ) {
                    return <Spinner />;
                  }

                  if (!featureGlobalRecordingOverridesEnabled) {
                    return <RedirectForbidden />;
                  }

                  return (
                    <FeatureErrorBoundary featureName='Global Recording Defaults'>
                      {children}
                    </FeatureErrorBoundary>
                  );
                },
              ),
          },
          {
            path: `${settingsPaths.individualRecording}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/IndividualRecordingDefaultsPage/IndividualRecordingDefaultsPage'
                  ),
                ),
                ({ children }) => {
                  const { isGlobalOverrideEnabled } =
                    useGlobalRecordingOverride();

                  if (isGlobalOverrideEnabled) {
                    return <RedirectForbidden />;
                  }

                  return (
                    <FeatureErrorBoundary featureName='Individual Recording Defaults'>
                      {children}
                    </FeatureErrorBoundary>
                  );
                },
              ),
          },
          {
            path: `${settingsPaths.queueRecording}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/QueueRecordingDefaultsPage/QueueRecordingDefaultsPage'
                  ),
                ),
                ({ children }) => {
                  const { isGlobalOverrideEnabled } =
                    useGlobalRecordingOverride();

                  if (isGlobalOverrideEnabled) {
                    return <RedirectForbidden />;
                  }

                  return (
                    <FeatureErrorBoundary featureName='Queue Recording Defaults'>
                      {children}
                    </FeatureErrorBoundary>
                  );
                },
              ),
          },
          {
            path: `${settingsPaths.recordingExclusionList}`,
            lazy: () =>
              lazyWrapComponent(
                lazyComponent(
                  import(
                    '@pages/settings/RecordingExclusionsListPage/RecordingDefaultsExclusionListPage'
                  ),
                ),
                ({ children }) => (
                  <FeatureErrorBoundary featureName='Recording Exclusion List'>
                    {children}
                  </FeatureErrorBoundary>
                ),
              ),
          },
        ],
      },
      {
        path: 'personalsettings',
        lazy: () =>
          lazyWrapComponent(
            lazyComponent(
              import('@pages/agent/PersonalSettingsPage/PersonalSettingsPage'),
            ),
            ({ children }) => (
              <FeatureErrorBoundary featureName='Personal Settings'>
                {children}
              </FeatureErrorBoundary>
            ),
          ),
      },
    ],
  },
  {
    path: '*',
    lazy: () =>
      lazyWrapComponent(
        lazyComponent(import('@pages/other/NotFoundPage/NotFoundPage')),
        ({ children }) => (
          <FeatureErrorBoundary featureName='NotFound'>
            {children}
          </FeatureErrorBoundary>
        ),
      ),
  },
];

export default mainRoutes;
